import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Table, Button, Modal, Form, Input, Select, Switch, Space, Tag, message } from 'antd';
import { EditOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiService } from '../services/api';
import { FajrParticipant, ResponsePageFajrParticipant, UpdateFajrParticipantRequest } from '../types/auth';
import { isAuthenticated } from '../utils/auth';

const { Search } = Input;

const formatDateTime = (dateTime: string) => new Date(dateTime).toLocaleString();

const FajrCity: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const editParticipantId = searchParams.get('edit');
  const loadedParticipantIdRef = useRef<string | null>(null);
  const [participantsData, setParticipantsData] = useState<ResponsePageFajrParticipant>({
    content: [],
    totalElements: 0,
    totalPages: 0,
    currentPage: 0,
    pageSize: 10
  });
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [approvedFilter, setApprovedFilter] = useState<boolean | undefined>(undefined);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [editingParticipant, setEditingParticipant] = useState<FajrParticipant | null>(null);
  const [editForm] = Form.useForm();

  const loadParticipants = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiService.getFajrParticipants(
        currentPage,
        pageSize,
        searchQuery || undefined,
        approvedFilter
      );
      setParticipantsData(response);
    } catch (error: any) {
      if (error.response?.status === 401) {
        navigate('/login');
      } else if (error.response?.status === 403) {
        message.error('Access denied. You do not have permission to view Mira Hills.');
        setParticipantsData({
          content: [],
          totalElements: 0,
          totalPages: 0,
          currentPage: 0,
          pageSize: 10
        });
      } else {
        message.error('Failed to load Mira Hills participants');
      }
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchQuery, approvedFilter, navigate]);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }
    loadParticipants();
  }, [loadParticipants, navigate]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, approvedFilter]);

  const handleTableChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const openEditModal = useCallback((participant: FajrParticipant) => {
    setEditingParticipant(participant);
    editForm.setFieldsValue({
      name: participant.name,
      email: participant.email,
      phone: participant.phone,
      type: participant.type,
      comment: participant.comment || '',
      approved: participant.approved
    });
    setIsEditModalVisible(true);
  }, [editForm]);

  const clearEditParam = useCallback(() => {
    if (!searchParams.has('edit')) return;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('edit');
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleEditParticipant = (participant: FajrParticipant) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('edit', participant.id);
    setSearchParams(nextParams);
  };

  const handleCloseEditModal = () => {
    setIsEditModalVisible(false);
    setEditingParticipant(null);
    editForm.resetFields();
    loadedParticipantIdRef.current = null;
    clearEditParam();
  };

  useEffect(() => {
    if (!editParticipantId) {
      loadedParticipantIdRef.current = null;
      setIsEditModalVisible(false);
      setEditingParticipant(null);
      editForm.resetFields();
      return;
    }

    if (loadedParticipantIdRef.current === editParticipantId) {
      return;
    }

    let cancelled = false;

    const openFromUrl = async () => {
      try {
        const participant = await apiService.getFajrParticipant(editParticipantId);
        if (cancelled) return;
        loadedParticipantIdRef.current = editParticipantId;
        openEditModal(participant);
      } catch (error: any) {
        if (cancelled) return;
        loadedParticipantIdRef.current = null;
        if (error.response?.status === 401) {
          navigate('/login');
        } else if (error.response?.status === 403) {
          message.error('Access denied. You do not have permission to view this participant.');
          clearEditParam();
        } else {
          message.error('Participant not found');
          clearEditParam();
        }
      }
    };

    openFromUrl();

    return () => {
      cancelled = true;
    };
  }, [editParticipantId, navigate, editForm, openEditModal, clearEditParam]);

  const handleUpdateParticipant = async (values: UpdateFajrParticipantRequest) => {
    if (!editingParticipant) return;

    setIsUpdating(true);
    try {
      await apiService.updateFajrParticipant(editingParticipant.id, values);
      message.success('Participant updated successfully');
      handleCloseEditModal();
      await loadParticipants();
    } catch (error: any) {
      if (error.response?.status === 401) {
        navigate('/login');
      } else if (error.response?.status === 403) {
        message.error('Access denied. You do not have permission to update participant.');
      } else {
        message.error(error.response?.data?.message || 'Failed to update participant');
      }
    } finally {
      setIsUpdating(false);
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      render: (id: string) => <span style={{ fontFamily: 'monospace' }}>{id}</span>
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email'
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone'
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type'
    },
    {
      title: 'Registration Date',
      dataIndex: 'registrationDate',
      key: 'registrationDate',
      render: (registrationDate: string) => formatDateTime(registrationDate)
    },
    {
      title: 'Approved',
      dataIndex: 'approved',
      key: 'approved',
      render: (approved: boolean) => (
        <Tag color={approved ? 'green' : 'orange'}>
          {approved ? 'Approved' : 'Pending'}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: FajrParticipant) => (
        <Button
          type="primary"
          icon={<EditOutlined />}
          size="small"
          onClick={() => handleEditParticipant(record)}
        >
          Edit
        </Button>
      )
    }
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>Mira Hills</h2>
        <Space size={16}>
          <Search
            placeholder="Search by name, email, phone or type"
            allowClear
            enterButton={<SearchOutlined />}
            size="large"
            style={{ width: 380 }}
            onSearch={setSearchQuery}
          />
          <Select
            placeholder="Approved"
            allowClear
            size="large"
            style={{ width: 170 }}
            value={approvedFilter}
            onChange={(value: boolean | undefined) => setApprovedFilter(value)}
            options={[
              { value: true, label: 'Approved' },
              { value: false, label: 'Not approved' }
            ]}
          />
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={participantsData.content}
        loading={loading}
        rowKey="id"
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: participantsData.totalElements,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} participants`,
          onChange: handleTableChange,
          onShowSizeChange: handleTableChange,
          pageSizeOptions: ['10', '20', '50', '100']
        }}
      />

      <Modal
        title={`Edit Participant - ${editingParticipant?.name || ''}`}
        open={isEditModalVisible}
        onCancel={handleCloseEditModal}
        footer={[
          <Button key="cancel" onClick={handleCloseEditModal} disabled={isUpdating}>
            Cancel
          </Button>,
          <Button key="save" type="primary" onClick={() => editForm.submit()} loading={isUpdating}>
            Save
          </Button>
        ]}
        width={640}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleUpdateParticipant}
        >
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: 'Please enter name' }]}
          >
            <Input placeholder="Enter name" />
          </Form.Item>

          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Please enter a valid email' }
            ]}
          >
            <Input placeholder="Enter email" />
          </Form.Item>

          <Form.Item
            label="Phone"
            name="phone"
            rules={[{ required: true, message: 'Please enter phone' }]}
          >
            <Input placeholder="Enter phone" />
          </Form.Item>

          <Form.Item
            label="Type"
            name="type"
            rules={[{ required: true, message: 'Please enter type' }]}
          >
            <Input placeholder="Enter type" />
          </Form.Item>

          <Form.Item
            label="Comment"
            name="comment"
          >
            <Input.TextArea placeholder="Enter comment" rows={4} />
          </Form.Item>

          <Form.Item label="Approved" name="approved" valuePropName="checked">
            <Switch checkedChildren="Yes" unCheckedChildren="No" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default FajrCity;
